/**
 * CreateArtifactDialog — modal para crear un artifact desde una plantilla
 * del snapshot del proyecto.
 *
 * Workflow:
 *  1. Consulta las plantillas agregables (`addable_in_phase`) de la fase
 *     desde la cual se abrió — mismo queryKey que usa `PhaseDrillIn` para
 *     gatear el botón, así que al abrir el modal el cache ya está tibio.
 *  2. Selecciona una plantilla → input nombre opcional → submit.
 *  3. POST /projects/{id}/artifacts (`custom_name` + `subphase_code`,
 *     este último tomado del `subphase_id` de la plantilla elegida) →
 *     invalida lifecycle + official queries → navega al editor del
 *     artifact recién creado.
 *
 * OT-0011 §4.2 — el diálogo se monta solo cuando está abierto
 * (`PhaseDrillIn` decide el mount/unmount). Por eso no recibe `open`:
 * si existe como componente, está abierto. `phaseCode` es requerida —
 * no hay selector manual de fase, el modal trabaja exclusivamente con
 * la fase desde la que se abrió.
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../../api/client'
import { createArtifact } from '../../../api/artifacts'
import {
  addableTemplatesQueryKey,
  fetchSnapshotTemplateSummaries,
  type SnapshotTemplateSummary,
} from '../../../api/projects'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { pushToast } from '../../../stores/toastStore'

interface CreateArtifactDialogProps {
  onClose: () => void
  projectId: string
  /** Idioma de los documentos oficiales del proyecto — para mostrar el
   * nombre/desc de la plantilla en ese idioma. Default 'es'. */
  contentLanguage?: 'es' | 'en'
  /** Fase desde la que se abrió el modal. El picker solo ofrece
   * plantillas agregables para esta fase — no hay forma de cambiarla
   * desde el diálogo. */
  phaseCode: string
}

export function CreateArtifactDialog({
  onClose,
  projectId,
  contentLanguage = 'es',
  phaseCode,
}: CreateArtifactDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [templateKey, setTemplateKey] = useState<string>('')
  const [customName, setCustomName] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // Mismo queryKey que `PhaseDrillIn` (`addableTemplatesQueryKey`) — el
  // botón que abre este modal ya forzó este fetch para decidir si se
  // mostraba, así que normalmente llega aquí con el cache tibio.
  const summariesQuery = useQuery({
    queryKey: addableTemplatesQueryKey(projectId, phaseCode),
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchSnapshotTemplateSummaries(accessToken, projectId, {
        template_type: 'artifact',
        addable_in_phase: phaseCode,
      })
    },
    enabled: !!accessToken,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
  })

  const addableTemplates: SnapshotTemplateSummary[] = summariesQuery.data ?? []

  const selectedTemplate = addableTemplates.find(
    (tpl) => tpl.template_key === templateKey,
  )

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      if (!templateKey) {
        setError(t('official_editor.create_dialog.errors.template_required'))
        throw new Error('validation')
      }
      const body = {
        template_key: templateKey,
        ...(customName.trim() ? { custom_name: customName.trim() } : {}),
        ...(selectedTemplate?.subphase_id
          ? { subphase_code: selectedTemplate.subphase_id }
          : {}),
      }
      return createArtifact(accessToken, projectId, body)
    },
    onSuccess: (artifact) => {
      pushToast({
        kind: 'success',
        message: t('official_editor.saved_toast'),
      })
      // Invalida queries que pintan listas de oficiales / lifecycle counts.
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'official'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'lifecycle'],
      })
      reset()
      onClose()
      // Navega al editor del nuevo draft. `search.v=undefined` — el
      // editor usará `current_version_id` del artifact (la v1 recién
      // creada). El `v` solo se setea cuando abrimos una draft
      // post-canon desde NewVersionDialog.
      void navigate({
        to: '/projects/$projectId/official/$artifactId/edit',
        params: { projectId, artifactId: artifact.id },
        search: { v: undefined },
      })
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'validation') return
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('official_editor.create_dialog.errors.submit_failed')
      // OT-0011 — el 409 ARTIFACT_TEMPLATE_NOT_REPEATABLE interpola
      // {{template_key}}; sin pasar `details`, quedaría sin rellenar
      // (mismo patrón que `client.ts` documenta: `t(error.code,
      // error.body?.details)`).
      const details =
        err instanceof ApiError && err.body?.details
          ? (err.body.details as Record<string, unknown>)
          : {}
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { ...details, defaultValue: fallback })
        : fallback
      setError(message)
    },
  })

  function reset() {
    setTemplateKey('')
    setCustomName('')
    setError(null)
  }

  function handleClose() {
    if (mutation.isPending) return
    reset()
    onClose()
  }

  const submitting = mutation.isPending

  return (
    <Dialog
      open
      onClose={handleClose}
      title={t('official_editor.create_dialog.title')}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('official_editor.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={submitting || !templateKey}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? t('official_editor.create_dialog.submitting')
              : t('official_editor.create_dialog.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="font-body text-sm text-ink-muted">
          {t('official_editor.create_dialog.subtitle')}
        </p>

        {/* Picker de plantilla — ya viene acotado a la fase actual
            (`addable_in_phase`); no hay selector manual de fase. */}
        <div>
          <p className="text-xs font-medium text-ink-muted mb-2">
            {t('official_editor.create_dialog.template_label')}
          </p>
          {summariesQuery.isLoading ? (
            <p className="font-mono text-xs text-ink-subtle">
              {t('official_viewer.loading')}
            </p>
          ) : addableTemplates.length === 0 ? (
            <p className="font-body text-sm text-ink-muted italic">
              {t('official_editor.create_dialog.no_templates_in_snapshot')}
            </p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {addableTemplates.map((tpl) => (
                <TemplateOption
                  key={tpl.template_key}
                  template={tpl}
                  selected={tpl.template_key === templateKey}
                  contentLanguage={contentLanguage}
                  onSelect={() => {
                    setTemplateKey(tpl.template_key)
                    if (error) setError(null)
                  }}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Nombre opcional */}
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('official_editor.create_dialog.name_label')}
          </span>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            disabled={submitting}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-50"
          />
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('official_editor.create_dialog.name_help')}
          </span>
        </label>

        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 font-mono text-xs text-critical">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}

// ─── Template option ────────────────────────────────────────────────────────
function TemplateOption({
  template,
  selected,
  contentLanguage,
  onSelect,
}: {
  template: SnapshotTemplateSummary
  selected: boolean
  contentLanguage: 'es' | 'en'
  onSelect: () => void
}) {
  const i18n = template.i18n ?? undefined
  const localeBlock =
    (i18n && (i18n[contentLanguage] || i18n['es'] || i18n['en'])) ?? null
  const name = localeBlock?.name ?? template.template_key
  const description = localeBlock?.description ?? ''
  const phaseId = template.phase_id ?? '—'

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={
          selected
            ? 'w-full text-left border border-brand bg-brand-soft/30 rounded-md p-3 transition-colors'
            : 'w-full text-left border border-line rounded-md p-3 hover:border-line-strong transition-colors'
        }
      >
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <span className="font-medium text-ink text-sm">{name}</span>
          <span className="font-mono text-[10px] text-ink-subtle">
            {template.template_key} · v{template.version} · {phaseId}
          </span>
        </div>
        {description ? (
          <p className="mt-1 font-body text-xs text-ink-muted">{description}</p>
        ) : null}
      </button>
    </li>
  )
}
